/**
 * The symbols of the list you were last on, as a wheel.
 *
 * Held open only while the grip is held, over the chart's lower half — where the
 * thumb already is. It offers **the list as shown**: the screener's current
 * frame, filter and sort included, which is what makes a wheel the right shape
 * for it. A short ordered set you were already looking through needs no keyboard
 * and no aiming; the whole universe would need both, and that is what tapping
 * the grip opens instead.
 *
 * ## It reports, and commits nothing
 *
 * The wheel says which row is under the band; `SymbolGrip` commits on release.
 * That split is not tidiness — `symbol` is in `ChartGroupView`'s `structuralKey`,
 * so a commit is a WebGL teardown plus a refetch, and committing per row crossed
 * would rebuild the surface once per symbol scrolled past.
 *
 * ## Why the list moves and the band does not
 *
 * A fixed band with a moving list is the physical metaphor and, more usefully,
 * means the selected row is always at one known place on screen: the arithmetic
 * is `round(offset / ITEM)` and nothing has to be measured. The alternative —
 * moving a highlight down a fixed list — has to know each row's position, which
 * is a measurement jsdom cannot make and a browser only makes after paint.
 */

/** One row's height. The wheel's arithmetic is in units of this. */
export const WHEEL_ITEM = 44;

export interface WheelSymbol {
  symbol: string;
  /** The company name, when the index knows one. */
  name?: string;
}

export interface SymbolWheelProps {
  symbols: readonly WheelSymbol[];
  /** Which row is under the band. */
  index: number;
}

/**
 * Which row a drag of `dy` pixels from `from` lands on.
 *
 * Up is forward through the list, as a physical wheel would turn: the fingertip
 * and the row it is dragging move together. Clamped rather than wrapped —
 * a wheel that loops has no edges to feel, and this one is short enough that
 * running off the end is an error rather than a shortcut.
 */
export function wheelIndex(from: number, dy: number, count: number): number {
  if (count <= 0) return 0;
  const raw = from + dy / WHEEL_ITEM;
  return Math.max(0, Math.min(count - 1, Math.round(raw)));
}

const SHEET: React.CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: "60%",
  zIndex: 8,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  overflow: "hidden",
  // Opaque under the rows and fading only at its top edge. A thinner scrim let
  // candles show through the symbols, and a wheel that cannot be read is not one.
  background:
    "linear-gradient(180deg, transparent, var(--qf-background) 26%, var(--qf-background))",
  // The grip owns the gesture; nothing here should take a touch of its own.
  pointerEvents: "none",
};

const ROW: React.CSSProperties = {
  height: WHEEL_ITEM,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0 22px",
  whiteSpace: "nowrap",
};

/** The band the wheel turns through. The row under it is the pick. */
const BAND: React.CSSProperties = {
  position: "absolute",
  left: 12,
  right: 12,
  top: "50%",
  height: WHEEL_ITEM,
  marginTop: -WHEEL_ITEM / 2,
  borderRadius: 9,
  border: "1px solid var(--qf-accent)",
  background: "var(--qf-selection)",
};

export function SymbolWheel({ symbols, index }: SymbolWheelProps) {
  return (
    <div style={SHEET} aria-hidden>
      <div style={BAND} />
      <div
        style={{
          position: "relative",
          // The band is fixed at the centre, so the list is shifted by the
          // selected row's own height. `calc` rather than a measured pixel
          // offset: the sheet's height is a percentage and only CSS knows it.
          transform: `translateY(calc(50% - ${WHEEL_ITEM / 2}px - ${index * WHEEL_ITEM}px))`,
        }}
      >
        {symbols.map((s, i) => {
          const on = i === index;
          return (
            <div
              key={s.symbol}
              style={{
                ...ROW,
                color: on ? "var(--qf-text)" : "var(--qf-text-muted)",
                fontSize: on ? 17 : 15,
              }}
            >
              <span
                style={{
                  fontWeight: 600,
                  minWidth: 64,
                  color: on ? "var(--qf-accent)" : "inherit",
                }}
              >
                {s.symbol}
              </span>
              <span
                style={{
                  fontSize: 11,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "var(--qf-text-muted)",
                }}
              >
                {s.name ?? ""}
              </span>
            </div>
          );
        })}
      </div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 8,
          textAlign: "center",
          fontSize: 10,
          letterSpacing: "0.06em",
          color: "var(--qf-text-muted)",
        }}
      >
        slide to choose · release to load
      </div>
    </div>
  );
}
